import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  Link,
  FormControl,
  FormHelperText,
  Alert
} from '@mui/material';
import { login } from '../api';
import { useAuth } from '../AuthContext';
import { useThemeContext } from '../ThemeContext';
import { useLang } from '../i18n';
import LangSwitch from '../components/LangSwitch';

function Login() {
  const { t } = useLang();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const { refreshThemeFromServer } = useThemeContext();

  const validateForm = () => {
    let isValid = true;

    if (!email) {
      setEmailError(t('auth.emailRequired'));
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError(t('auth.emailInvalid'));
      isValid = false;
    } else {
      setEmailError('');
    }

    if (!password) {
      setPasswordError(t('auth.passwordRequired'));
      isValid = false;
    } else if (password.length < 6) {
      setPasswordError(t('auth.passwordShort'));
      isValid = false;
    } else {
      setPasswordError('');
    }

    return isValid;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    try {
      const { access_token, name: userName, role } = await login(email, password);
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('userName', userName);
      localStorage.setItem('userRole', role);
      await refreshThemeFromServer();
      authLogin(); // Обновляем состояние аутентификации
      navigate('/');
    } catch (err) {
      if (err.response && err.response.status === 403) {
        setError(t('auth.notVerified'));
      } else {
        setError(t('auth.wrongCredentials'));
      }
    }
  };

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 4,
          boxShadow: 3,
          borderRadius: 2,
          backgroundColor: 'background.paper'
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
          {t('auth.loginTitle')}
        </Typography>
        <LangSwitch />

        {error && (
          <Alert severity="error" sx={{ mb: 2, width: '100%' }}>{error}</Alert>
        )}

        <Box
          component="form"
          onSubmit={handleLogin}
          sx={{ width: '100%' }}
        >
          <FormControl fullWidth margin="normal" error={!!emailError}>
            <TextField
              label={t('auth.email')}
              variant="outlined"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!emailError}
            />
            {emailError && <FormHelperText>{emailError}</FormHelperText>}
          </FormControl>

          <FormControl fullWidth margin="normal" error={!!passwordError}>
            <TextField
              label={t('auth.password')}
              type="password"
              variant="outlined"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!passwordError}
            />
            {passwordError && <FormHelperText>{passwordError}</FormHelperText>}
          </FormControl>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            sx={{ mt: 3, mb: 2 }}
          >
            {t('auth.loginButton')}
          </Button>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link href="/forgot-password" variant="body2">
              {t('auth.forgot')}
            </Link>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link href="/register" variant="body2">
              {t('auth.noAccount')}
            </Link>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}

export default Login;
